import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useSuspenseQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import dayjs from 'dayjs';

import { Table, TableHeader, TableHead, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { AssetContentTypeEnum, AssetsOrderByEnum, ContentTypeEnum, DepositStatusEnum } from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';
import { withSuspense } from 'src/hoc';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { contentTypeMap } from 'src/containers/SoftwareEscrow/Deposits';

type ITableRow = {
  contentType: AssetContentTypeEnum | null | undefined;
  assetsCount?: number;
  latestDeposit?: string;
};

const columnHelper = createColumnHelper<ITableRow>();

const columns = [
  columnHelper.accessor('contentType', {
    header: 'Asset type',
    cell: (props) => {
      const value = props.getValue();

      return value && value in contentTypeMap ? contentTypeMap[value as keyof typeof contentTypeMap] : 'Unlabeled';
    },
  }),
  columnHelper.accessor('assetsCount', {
    header: 'Assets',
    cell: (props) => props.getValue(),
  }),
  columnHelper.accessor('latestDeposit', {
    header: 'Last deposit',
    cell: (props) => (props.getValue() ? dayjs(props.getValue()).format('MMM DD, YYYY') : '-'),
  }),
];

const AssetsTable = () => {
  const {
    softwareEscrow: { id: softwareEscrowId, requiredContentTypes },
  } = useSoftwareEscrow();
  const { data: unlabelledTakenAssets } = useSuspenseQuery(AssetsDocument, {
    variables: {
      softwareEscrowId,
      labeled: false,
      depositStatus: DepositStatusEnum.Taken,
      orderBy: AssetsOrderByEnum.LatestDeposit,
      perPage: 1,
    },
  });
  const { data: sourceCodeTakenAssets } = useSuspenseQuery(AssetsDocument, {
    variables: {
      depositStatus: DepositStatusEnum.Taken,
      softwareEscrowId,
      perPage: 1,
      contentType: AssetContentTypeEnum.SourceCode,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
    skip: !requiredContentTypes.includes(ContentTypeEnum.SourceCode),
  });
  const { data: documentationTakenAssets } = useSuspenseQuery(AssetsDocument, {
    variables: {
      depositStatus: DepositStatusEnum.Taken,
      softwareEscrowId,
      perPage: 1,
      contentType: AssetContentTypeEnum.Documentation,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
    skip: !requiredContentTypes.includes(ContentTypeEnum.Documentation),
  });
  const { data: dataTakenAssets } = useSuspenseQuery(AssetsDocument, {
    variables: {
      depositStatus: DepositStatusEnum.Taken,
      softwareEscrowId,
      perPage: 1,
      contentType: AssetContentTypeEnum.Data,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
    skip: !requiredContentTypes.includes(ContentTypeEnum.Data),
  });

  const assetsMap = {
    ...(Boolean(unlabelledTakenAssets?.assets.nodesCount) && {
      unlabeled: {
        contentType: undefined,
        assetsCount: unlabelledTakenAssets?.assets.nodesCount,
        latestDeposit: unlabelledTakenAssets?.assets.nodes[0]?.latestDeposit,
      },
    }),
    [AssetContentTypeEnum.SourceCode]: {
      contentType: AssetContentTypeEnum.SourceCode,
      assetsCount: sourceCodeTakenAssets?.assets.nodesCount,
      latestDeposit: sourceCodeTakenAssets?.assets.nodes[0]?.latestDeposit,
    },
    [AssetContentTypeEnum.Documentation]: {
      contentType: AssetContentTypeEnum.Documentation,
      assetsCount: documentationTakenAssets?.assets.nodesCount,
      latestDeposit: documentationTakenAssets?.assets.nodes[0]?.latestDeposit,
    },
    [AssetContentTypeEnum.Data]: {
      contentType: AssetContentTypeEnum.Data,
      assetsCount: dataTakenAssets?.assets.nodesCount,
      latestDeposit: dataTakenAssets?.assets.nodes[0]?.latestDeposit,
    },
  };

  const tableData = useMemo(
    () =>
      Object.values(assetsMap).filter(
        ({ assetsCount }) => typeof assetsCount === 'number' && Number.isFinite(assetsCount),
      ),
    [],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  className="border-r last:border-r-0 bg-accent px-2 h-[27px] text-2xs text-foreground font-bold"
                  key={header.id}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="hover:bg-transparent">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="border-r last:border-r-0 p-2 text-2xs text-foreground">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="p-2 text-2xs text-foreground bg-white text-center">
                No assets
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default withSuspense(AssetsTable);
